import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Flex,
  Text,
  Card,
  Button,
  Heading,
  Table,
  Badge,
  IconButton,
  TextField,
  Callout,
} from '@radix-ui/themes';
import SectionHeading from './common/SectionHeading';
import {
  CheckIcon,
  Cross2Icon,
  ExclamationTriangleIcon
} from '@radix-ui/react-icons';
import { ProblemApprovalItem, MedicationApprovalItem } from '../types/patient';
import { COMMON_ICD10_CODES, getSuggestedICD10Codes, ICD10Code, isSupportedDisease } from '../constants/icd10Codes';
import { isUndecidedNewItem } from '../utils/carePlanRefreshDiff';

interface ApprovalWorkflowProps {
  problems: ProblemApprovalItem[];
  medications: MedicationApprovalItem[];
  onProblemsChange: (problems: ProblemApprovalItem[]) => void;
  onMedicationsChange: (medications: MedicationApprovalItem[]) => void;
  onApprove: () => void;
  loading?: boolean;
}

/**
 * PHA-1046: how this item stood on the LAST APPROVED care plan.
 *
 * Only set in the care-plan refresh flow (`buildRefreshReviewList`); first-time onboarding
 * leaves `priorState` undefined and renders nothing extra, which keeps the creation
 * workflow visually unchanged (PHA-1033 requires that).
 */
const PriorStateBadge: React.FC<{ item: any }> = ({ item }) => {
  switch (item?.priorState) {
    case 'kept':
      return (
        <Badge color="green" size="1" variant="soft" title="On the current care plan">
          ✓ On current plan
        </Badge>
      );
    case 'rejected':
      return (
        <Badge
          color="red"
          size="1"
          variant="soft"
          title="Rejected when the current care plan was approved. Tick the checkbox to include it this time."
        >
          ✗ Previously rejected
        </Badge>
      );
    case 'new':
      return (
        <Badge color="blue" size="1" variant="solid" title="Not on the current care plan">
          NEW
        </Badge>
      );
    default:
      return null;
  }
};

const ApprovalWorkflow: React.FC<ApprovalWorkflowProps> = ({
  problems,
  medications,
  onProblemsChange,
  onMedicationsChange,
  onApprove,
  loading = false
}) => {
  const [icd10DropdownIndex, setIcd10DropdownIndex] = useState<number | null>(null);
  const [icd10SearchTerm, setIcd10SearchTerm] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIcd10DropdownIndex(null);
        setIcd10SearchTerm('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const setProblemApproval = (index: number, value: boolean) => {
    const updatedProblems = problems.map((problem, i) =>
      i === index ? { ...problem, approved: value } : problem
    );
    onProblemsChange(updatedProblems);
  };

  const setMedicationApproval = (index: number, value: boolean) => {
    const updatedMedications = medications.map((medication, i) =>
      i === index ? { ...medication, approved: value } : medication
    );
    onMedicationsChange(updatedMedications);
  };

  const approveAllProblems = () => {
    const updatedProblems = problems.map(problem => ({ ...problem, approved: true }));
    onProblemsChange(updatedProblems);
  };

  const rejectAllProblems = () => {
    const updatedProblems = problems.map(problem => ({ ...problem, approved: false }));
    onProblemsChange(updatedProblems);
  };

  const approveAllMedications = () => {
    const updatedMedications = medications.map(medication => ({ ...medication, approved: true }));
    onMedicationsChange(updatedMedications);
  };

  const rejectAllMedications = () => {
    const updatedMedications = medications.map(medication => ({ ...medication, approved: false }));
    onMedicationsChange(updatedMedications);
  };

  // PHA-955: a cleared <input type="date"> emits '', which the backend stores
  // verbatim in extracted_data JSONB — and every reader casts those keys to a
  // real `date`, so one blank value 500s care-plan generation for the patient.
  // Send null instead of ''.
  const DATE_FIELDS = ['onset_date', 'start_date', 'end_date'];
  const normalizeFieldValue = (field: string, value: any) =>
    DATE_FIELDS.includes(field) && typeof value === 'string' && !value.trim() ? null : value;

  const updateProblem = (index: number, field: keyof ProblemApprovalItem, value: any) => {
    const nextValue = normalizeFieldValue(field as string, value);
    const updatedProblems = problems.map((problem, i) =>
      i === index ? { ...problem, [field]: nextValue } : problem
    );
    onProblemsChange(updatedProblems);
  };

  const updateMedication = (index: number, field: keyof MedicationApprovalItem, value: any) => {
    const nextValue = normalizeFieldValue(field as string, value);
    const updatedMedications = medications.map((medication, i) =>
      i === index ? { ...medication, [field]: nextValue } : medication
    );
    onMedicationsChange(updatedMedications);
  };

  const selectICD10Code = (problemIndex: number, code: ICD10Code) => {
    const updatedProblems = problems.map((problem, i) =>
      i === problemIndex
        ? { ...problem, icd10_code: code.code, icd10_description: code.description, icd10_needs_review: false }
        : problem
    );
    onProblemsChange(updatedProblems);
    setIcd10DropdownIndex(null);
    setIcd10SearchTerm('');
  };

  const getFilteredICD10Suggestions = (problemName: string, searchOverride?: string): ICD10Code[] => {
    const suggested = getSuggestedICD10Codes(problemName);
    const term = searchOverride ?? icd10SearchTerm;
    const searchLower = term.toLowerCase();

    // Combine suggested codes with common codes, removing duplicates
    const allCodes = [...suggested];
    for (const code of COMMON_ICD10_CODES) {
      if (!allCodes.some(c => c.code === code.code)) {
        allCodes.push(code);
      }
    }

    if (!term) return allCodes;

    return allCodes.filter(
      c => c.code.toLowerCase().includes(searchLower) || c.description.toLowerCase().includes(searchLower)
    );
  };

  // Validation: all approved problems must have ICD-10 codes
  const hasApprovedProblems = problems.some(p => p.approved === true);
  // PHA-1030: medications are OPTIONAL. Plenty of patients have nothing on file in
  // the EHR (and a clinician may legitimately reject every stale med a refresh pulled
  // in), and there is no way to add one by hand — so requiring an approved medication
  // meant those patients could not be onboarded at all. The backend already supports
  // an empty medication list end to end: update_patient_data_approval() no-ops on an
  // empty batch, care-plan generation has no medication assertion, and the prompt
  // drops the adherence question when the list is empty.
  const approvedProblemsWithoutICD10 = problems.filter(p => p.approved === true && !p.icd10_code && isSupportedDisease(p.problem));
  const allApprovedProblemsHaveICD10 = approvedProblemsWithoutICD10.length === 0;
  // PHA-1141: in an EHR-sourced regeneration, NEW items start undecided and must each be
  // accepted or rejected — nothing from the EHR is accepted automatically.
  const undecidedNewCount = [...problems, ...medications].filter(isUndecidedNewItem).length;
  const canProceed = hasApprovedProblems && allApprovedProblemsHaveICD10 && undecidedNewCount === 0;

  return (
    <Card className="p-6">
      <Heading size="5" className="text-slate-800 mb-4">
        Review Patient Information
      </Heading>
      <Text size="3" className="text-slate-600 mb-8 block">
        Please approve/reject items from the lists below. At least one problem must be approved to generate a care plan; medications are optional. ICD-10 codes are required for Hypertension, Hyperlipidemia, Diabetes, CHF, COPD, and Atrial Fibrillation.
      </Text>

      {/* Problem List */}
      <Box className="mb-6">
        <Flex justify="between" align="center" className="mb-4">
          <SectionHeading noTopMargin>
            Problem List ({problems.filter(p => p.approved === true).length} approved)
          </SectionHeading>
          <Flex gap="2" align="center">
            {problems.length > 0 && (
              <>
                <Button
                  variant="soft"
                  size="2"
                  color="green"
                  onClick={approveAllProblems}
                >
                  <CheckIcon width="14" height="14" />
                  Approve All
                </Button>
                <Button
                  variant="soft"
                  size="2"
                  color="red"
                  onClick={rejectAllProblems}
                >
                  <Cross2Icon width="14" height="14" />
                  Reject All
                </Button>
              </>
            )}
          </Flex>
        </Flex>

        {problems.length > 0 ? (
          <Card className="overflow-x-auto">
            <Table.Root>
              <Table.Header>
                <Table.Row className="bg-slate-50">
                  <Table.ColumnHeaderCell className="w-32">Status</Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell style={{ minWidth: '250px' }}>Problem</Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell style={{ width: '160px' }}>ICD-10 Code</Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell style={{ width: '100px' }}>SNOMED Code</Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell className="w-24">Clinical Status</Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell className="w-32">Onset Date</Table.ColumnHeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {problems.map((problem, index) => (
                  <Table.Row key={index} className="hover:bg-slate-50">
                    <Table.Cell>
                      <Flex gap="1" align="center" justify="start">
                        <IconButton
                          size="1"
                          variant="soft"
                          color="green"
                          onClick={() => setProblemApproval(index, true)}
                          title="Approve"
                          style={{
                            border: '2px solid',
                            borderColor: problem.approved === true ? '#16a34a' : '#86efac',
                            backgroundColor: problem.approved === true ? '#dcfce7' : 'transparent',
                            borderRadius: '4px',
                            minWidth: '24px',
                            minHeight: '24px'
                          }}
                        >
                          <CheckIcon width="12" height="12" />
                        </IconButton>
                        <IconButton
                          size="1"
                          variant="soft"
                          color="red"
                          onClick={() => setProblemApproval(index, false)}
                          title="Reject"
                          style={{
                            border: '2px solid',
                            borderColor: problem.approved === false ? '#dc2626' : '#fca5a5',
                            backgroundColor: problem.approved === false ? '#fee2e2' : 'transparent',
                            borderRadius: '4px',
                            minWidth: '24px',
                            minHeight: '24px'
                          }}
                        >
                          <Cross2Icon width="12" height="12" />
                        </IconButton>
                      </Flex>
                    </Table.Cell>
                    <Table.Cell>
                      <Flex direction="column" gap="1">
                        <Flex gap="2" align="center">
                          <textarea
                            className="problem-input textarea-autosize"
                            rows={1}
                            value={problem.problem}
                            onChange={(e) => updateProblem(index, 'problem', e.target.value)}
                            style={(problem as any).isNew ? { backgroundColor: '#e8f5e9' } : (problem as any).isModified ? { backgroundColor: '#fff9c4' } : {}}
                          />
                          <PriorStateBadge item={problem} />
                          {(problem as any).isNew && (
                            <Badge color="green" size="1" variant="solid">New</Badge>
                          )}
                          {(problem as any).isModified && (
                            <Badge color="yellow" size="1" variant="solid">Modified</Badge>
                          )}
                        </Flex>
                        {(problem as any).isModified && (problem as any).originalValue && (
                          <Text size="1" style={{ color: '#666', fontStyle: 'italic' }}>
                            Original: {(problem as any).originalValue.problem}
                          </Text>
                        )}
                      </Flex>
                    </Table.Cell>
                    <Table.Cell>
                      <div style={{ position: 'relative' }} ref={icd10DropdownIndex === index ? dropdownRef : undefined}>
                        <Flex direction="column" gap="1">
                          <Flex gap="1" align="center">
                            <TextField.Root size="2" style={{ flex: 1 }}>
                              <TextField.Input
                                value={problem.icd10_code || ''}
                                onChange={(e) => {
                                  const val = (e.target as HTMLInputElement).value;
                                  const updatedProblems = problems.map((p, i) =>
                                    i === index ? { ...p, icd10_code: val, icd10_description: '', icd10_needs_review: true } : p
                                  );
                                  onProblemsChange(updatedProblems);
                                  setIcd10SearchTerm(val);
                                }}
                                onFocus={() => {
                                  setIcd10DropdownIndex(index);
                                  setIcd10SearchTerm(problem.icd10_code || '');
                                }}
                                placeholder="Code"
                                style={{
                                  backgroundColor: problem.approved === true && !problem.icd10_code && isSupportedDisease(problem.problem) ? '#fef2f2' : undefined,
                                  borderColor: problem.approved === true && !problem.icd10_code && isSupportedDisease(problem.problem) ? '#ef4444' : undefined,
                                }}
                              />
                            </TextField.Root>
                            {problem.icd10_needs_review && problem.icd10_code && (
                              <Badge color="yellow" size="1" variant="soft" title="ICD-10 code needs clinician review">
                                Review
                              </Badge>
                            )}
                          </Flex>
                          {problem.icd10_description && (
                            <Text size="1" style={{ color: '#666' }}>
                              {problem.icd10_description}
                            </Text>
                          )}
                        </Flex>
                        {/* ICD-10 suggestion dropdown */}
                        {icd10DropdownIndex === index && (
                          <div style={{
                            position: 'absolute',
                            top: '100%',
                            left: 0,
                            right: 0,
                            zIndex: 50,
                            backgroundColor: 'white',
                            border: '1px solid #e2e8f0',
                            borderRadius: '6px',
                            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                            maxHeight: '200px',
                            overflowY: 'auto',
                            marginTop: '2px',
                            minWidth: '300px',
                          }}>
                            {getFilteredICD10Suggestions(problem.problem, problem.icd10_code || '').map((code) => (
                              <div
                                key={code.code}
                                onClick={() => selectICD10Code(index, code)}
                                style={{
                                  padding: '6px 8px',
                                  cursor: 'pointer',
                                  fontSize: '12px',
                                  borderBottom: '1px solid #f1f5f9',
                                  backgroundColor: problem.icd10_code === code.code ? '#eff6ff' : undefined,
                                }}
                                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f8fafc')}
                                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = problem.icd10_code === code.code ? '#eff6ff' : '')}
                              >
                                <Flex gap="2" align="center">
                                  <Badge color="blue" size="1" variant="soft">{code.code}</Badge>
                                  <Text size="1">{code.description}</Text>
                                </Flex>
                              </div>
                            ))}
                            {getFilteredICD10Suggestions(problem.problem, problem.icd10_code || '').length === 0 && (
                              <div style={{ padding: '8px', textAlign: 'center' }}>
                                <Text size="1" style={{ color: '#94a3b8' }}>No matching codes. Your custom code will be used.</Text>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </Table.Cell>
                    <Table.Cell>
                      {problem.snomed_code ? (
                        <Flex direction="column" gap="1">
                          <Badge color="purple" size="1" variant="soft">{problem.snomed_code}</Badge>
                          {problem.snomed_description && (
                            <Text size="1" style={{ color: '#666' }}>
                              {problem.snomed_description}
                            </Text>
                          )}
                        </Flex>
                      ) : (
                        <Text size="1" style={{ color: '#94a3b8' }}>-</Text>
                      )}
                    </Table.Cell>
                    <Table.Cell>
                      <Badge color="green" variant="soft" size="1">
                        {problem.clinical_status || 'active'}
                      </Badge>
                    </Table.Cell>
                    <Table.Cell>
                      <TextField.Root size="2" className="w-full">
                        <TextField.Input
                          type="date"
                          value={problem.onset_date || new Date().toISOString().split('T')[0]}
                          onChange={(e) => updateProblem(index, 'onset_date', (e.target as HTMLInputElement).value)}
                        />
                      </TextField.Root>
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table.Root>
          </Card>
        ) : (
          <Card className="p-4 bg-slate-50">
            <Text className="text-slate-500 text-center">No problems extracted from documents</Text>
          </Card>
        )}
      </Box>

      {/* Medication List */}
      <Box className="mb-6">
        <Flex justify="between" align="center" className="mb-4">
          <SectionHeading>
            Medication List ({medications.filter(m => m.approved === true).length} approved)
          </SectionHeading>
          <Flex gap="2" align="center">
            {medications.length > 0 && (
              <>
                <Button
                  variant="soft"
                  size="2"
                  color="green"
                  onClick={approveAllMedications}
                >
                  <CheckIcon width="14" height="14" />
                  Approve All
                </Button>
                <Button
                  variant="soft"
                  size="2"
                  color="red"
                  onClick={rejectAllMedications}
                >
                  <Cross2Icon width="14" height="14" />
                  Reject All
                </Button>
              </>
            )}
          </Flex>
        </Flex>

        {medications.length > 0 ? (
          <Card className="overflow-hidden">
            <Table.Root>
              <Table.Header>
                <Table.Row className="bg-slate-50">
                  <Table.ColumnHeaderCell className="w-32">Status</Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell>Medication</Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell className="w-32">Dosage</Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell className="w-32">Start Date</Table.ColumnHeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {medications.map((medication, index) => (
                  <Table.Row key={index} className="hover:bg-slate-50">
                    <Table.Cell>
                      <Flex gap="1" align="center" justify="start">
                        <IconButton
                          size="1"
                          variant="soft"
                          color="green"
                          onClick={() => setMedicationApproval(index, true)}
                          title="Approve"
                          style={{
                            border: '2px solid',
                            borderColor: medication.approved === true ? '#16a34a' : '#86efac',
                            backgroundColor: medication.approved === true ? '#dcfce7' : 'transparent',
                            borderRadius: '4px',
                            minWidth: '24px',
                            minHeight: '24px'
                          }}
                        >
                          <CheckIcon width="12" height="12" />
                        </IconButton>
                        <IconButton
                          size="1"
                          variant="soft"
                          color="red"
                          onClick={() => setMedicationApproval(index, false)}
                          title="Reject"
                          style={{
                            border: '2px solid',
                            borderColor: medication.approved === false ? '#dc2626' : '#fca5a5',
                            backgroundColor: medication.approved === false ? '#fee2e2' : 'transparent',
                            borderRadius: '4px',
                            minWidth: '24px',
                            minHeight: '24px'
                          }}
                        >
                          <Cross2Icon width="12" height="12" />
                        </IconButton>
                      </Flex>
                    </Table.Cell>
                    <Table.Cell>
                      <Flex direction="column" gap="1">
                        <Flex gap="2" align="center">
                          <TextField.Root size="2" className="flex-1">
                            <TextField.Input
                              value={medication.medication}
                              onChange={(e) => updateMedication(index, 'medication', (e.target as HTMLInputElement).value)}
                              style={(medication as any).isNew ? { backgroundColor: '#e8f5e9' } : (medication as any).isModified ? { backgroundColor: '#fff9c4' } : {}}
                            />
                          </TextField.Root>
                          <PriorStateBadge item={medication} />
                          {(medication as any).isNew && (
                            <Badge color="green" size="1" variant="solid">New</Badge>
                          )}
                          {(medication as any).isModified && (
                            <Badge color="yellow" size="1" variant="solid">Modified</Badge>
                          )}
                        </Flex>
                        {(medication as any).isModified && (medication as any).originalValue && (
                          <Text size="1" style={{ color: '#666', fontStyle: 'italic' }}>
                            Original: {(medication as any).originalValue.medication}
                            {(medication as any).originalValue.dosage && ` - ${(medication as any).originalValue.dosage}`}
                          </Text>
                        )}
                      </Flex>
                    </Table.Cell>
                    <Table.Cell>
                      <TextField.Root size="2" className="w-full">
                        <TextField.Input
                          value={medication.dosage || ''}
                          onChange={(e) => updateMedication(index, 'dosage', (e.target as HTMLInputElement).value)}
                          placeholder="Enter dosage"
                        />
                      </TextField.Root>
                    </Table.Cell>
                    <Table.Cell>
                      <TextField.Root size="2" className="w-full">
                        <TextField.Input
                          type="date"
                          value={medication.start_date || new Date().toISOString().split('T')[0]}
                          onChange={(e) => updateMedication(index, 'start_date', (e.target as HTMLInputElement).value)}
                        />
                      </TextField.Root>
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table.Root>
          </Card>
        ) : (
          <Card className="p-4 bg-slate-50">
            <Text className="text-slate-500 text-center">
              No medications extracted from documents. Medications are optional — you can continue without them.
            </Text>
          </Card>
        )}
      </Box>

      {/* Action Button */}
      <Flex justify="center" className="mt-6">
        <Button
          size="3"
          disabled={!canProceed || loading}
          onClick={onApprove}
          className="min-w-48"
        >
          {loading ? (
            <div className="flex items-center gap-2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              Processing...
            </div>
          ) : (
            `Approve Problems/Medications`
          )}
        </Button>
      </Flex>

      {!canProceed && (() => {
        const errors: string[] = [];
        if (!hasApprovedProblems) {
          errors.push(problems.length === 0
            ? "No problems available. At least one problem is required."
            : "Please approve at least one problem.");
        }
        if (!allApprovedProblemsHaveICD10) {
          errors.push(`${approvedProblemsWithoutICD10.length} approved problem(s) missing ICD-10 codes. ICD-10 codes are required for Hypertension, Hyperlipidemia, Diabetes, CHF, COPD, and Atrial Fibrillation.`);
        }
        if (undecidedNewCount > 0) {
          errors.push(`${undecidedNewCount} item(s) marked NEW still need to be accepted or rejected.`);
        }
        return errors.length > 0 ? (
          <Callout.Root color="red" className="mt-4">
            <Callout.Icon>
              <ExclamationTriangleIcon />
            </Callout.Icon>
            <Callout.Text>
              {errors.join(' ')}
            </Callout.Text>
          </Callout.Root>
        ) : null;
      })()}
    </Card>
  );
};

export default ApprovalWorkflow;
